import React, {useCallback, useState} from 'react';
import Modal from './Modal';
import PrimaryButton from './PrimaryButton';
import {useMutation, useQueryClient} from 'react-query';
import {
  createBulkRuleFromTemplate,
  createFlow,
} from '../requests/queries/flows';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

function NameFlowTemplateModal({open, setOpen, template}) {
  const [flowName, setFlowName] = useState('');
  const {userDetails, user} = useSelector(state => state?.auth);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {mutate: createFlowFromTemplate, isLoading: createLoading} =
    useMutation('create-e-flow', createFlow);

  const {mutate: createBulkRule, isLoading: bulkLoading} = useMutation(
    'create-bulk-rule',
    createBulkRuleFromTemplate,
  );

  const handleIconClick = useCallback(
    flow => {
      navigate(`/flows/edit-process/${flow?.id}`, {
        state: {
          flowName: flow?.name || '-',
          flowData: flow,
        },
      });
      ['currentFlowData', 'taskId', 'ruleIndex', 'isModified'].forEach(key =>
        localStorage.removeItem(key),
      );
      localStorage.setItem('currentFlowData', JSON.stringify(flow));
    },
    [navigate],
  );

  const isLoading = createLoading || bulkLoading;

  const handleUseFlow = useCallback(async () => {
    try {
      const data = {
        name: flowName,
        usecase: template?.usecase || '',
        desc: template?.desc || flowName || '',
        company_id: userDetails?.company?.id,
      };

      await createFlowFromTemplate(data, {
        onSuccess: async data => {
          queryClient.invalidateQueries('pr-company-details');

          if (data) {
            const bulkData = {
              template_project_id: template?.id,
              project_id: data?.entity?.id,
              created_by: user?.id,
            };

            await createBulkRule(bulkData, {
              onSuccess: () => {
                toast.success('Rules created successfully!');
                setFlowName('');
                setOpen(false);
                handleIconClick(data?.entity);
              },
              onError: error => {
                toast.error(error?.response?.data?.error);
              },
            });
          }
        },
        onError: error => {
          toast.error(error?.response?.data?.error);
        },
      });
    } catch (e) {
      console.log(e);
    }
  }, [
    flowName,
    template?.usecase,
    template?.desc,
    template?.id,
    userDetails?.company?.id,
    createFlowFromTemplate,
    user?.id,
    createBulkRule,
    setOpen,
    handleIconClick,
    queryClient,
  ]);

  const handleModalClose = () => {
    setOpen(false);
  };

  return (
    <Modal
      show={open}
      onClose={handleModalClose}
      extraModalClasses="max-w-[444px] overflow-y-auto h-fit max-h-[95%] sm:max-h-full rounded-b-none sm:rounded-b-lg absolute bottom-0 sm:relative lookup-modal"
      modalPosition="justify-center sm:justify-end"
      modalTitle="Name Your Flow"
    >
      <div className="mt-2">
        <p className="text-left border-[0.5px] border-transparent border-b-[#EFEFEF] text-[#344272] text-tiny font-normal px-6 py-1">
          Give your new flow a name, we&apos;ll add the template rules to your
          new flow
        </p>

        <div className="py-2 px-6">
          <label htmlFor="name">
            <span className="text-sm">Flow name</span>
            <input
              type="text"
              name="name"
              className="mt-2"
              value={flowName}
              onChange={e => setFlowName(e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="w-full px-6 py-[14px] border-[0.5px] flex gap-4 border-transparent border-t-[#EFEFEF]">
        <PrimaryButton
          buttonText="Confirm"
          className="w-full !h-10"
          loading={isLoading}
          disabled={isLoading || !flowName}
          onClick={handleUseFlow}
        />
      </div>
    </Modal>
  );
}

export default NameFlowTemplateModal;
